import type { UserSettings } from '@/stores/auth'

export const BAR_SLOTS = 3

export type FooterBarPrefs = NonNullable<UserSettings['sidebarBar']>

export const DEFAULT_FOOTER_BAR_PREFS: FooterBarPrefs = { order: [], visible: {} }

export interface ArrangeableItem {
	id: string
	shownByDefault: boolean
}

export interface ArrangedItems<T> {
	shown: T[]
	bar: T[]
	menu: T[]
	hidden: T[]
}

/** `items` arrive in default order. Items missing from this page are skipped, so the next one flattens into the bar. */
export function arrangeFooterItems<T extends ArrangeableItem>(items: T[], prefs: FooterBarPrefs): ArrangedItems<T> {
	const rank = (item: T, index: number) => {
		const chosen = prefs.order.indexOf(item.id)
		return chosen === -1 ? prefs.order.length + index : chosen
	}
	const sorted = items
		.map((item, index) => ({ item, rank: rank(item, index) }))
		.sort((a, b) => a.rank - b.rank)
		.map(entry => entry.item)
	const isVisible = (item: T) => prefs.visible[item.id] ?? item.shownByDefault
	const shown = sorted.filter(isVisible)
	// An empty menu is never a deliberate choice, so fall back to the defaults.
	if (shown.length === 0 && sorted.length > 0 && prefs !== DEFAULT_FOOTER_BAR_PREFS) {
		return arrangeFooterItems(items, DEFAULT_FOOTER_BAR_PREFS)
	}
	return {
		shown,
		bar: shown.slice(0, BAR_SLOTS),
		menu: shown.slice(BAR_SLOTS),
		hidden: sorted.filter(item => !isVisible(item)),
	}
}

/** Ids from other pages keep their place after this page's. */
export function rearrange(prefs: FooterBarPrefs, shownIds: string[], hiddenIds: string[]): FooterBarPrefs {
	const pageIds = [...shownIds, ...hiddenIds]
	return {
		order: [...pageIds, ...prefs.order.filter(id => !pageIds.includes(id))],
		visible: { ...prefs.visible, ...Object.fromEntries(pageIds.map(id => [id, shownIds.includes(id)])) },
	}
}

/** Sits in the Customize list between shown and hidden ids. */
export const NOT_IN_MENU = 'not-in-menu'

/** What a screen reader hears about where `id` sits in `ids`. */
export function describePlace(ids: string[], id: string): string {
	const index = ids.indexOf(id)
	if (index > ids.indexOf(NOT_IN_MENU)) {
		return 'not in the menu'
	}
	return `${index < BAR_SLOTS ? 'in the bar' : 'in the menu'}, position ${index + 1}`
}
